import { z } from "zod";

/** Request bodies (PRD §13). */

export const AisType = z.enum([
  "weather_hood_big",
  "weather_hood_small",
  "table_filter_house",
  "vertical_filter_house",
  "trent_style",
  "unknown",
]);

export const SitePatch = z
  .object({
    ais_type: AisType.optional(),
    coordinates: z
      .object({
        lat: z.number().min(-90).max(90),
        lon: z.number().min(-180).max(180),
        precision_m: z.number().int().positive().max(5000).default(50),
      })
      .optional(),
    aliases: z.array(z.string().min(1).max(120)).max(20).optional(),
    note: z.string().max(300).optional(),
  })
  .refine(
    (b) => b.ais_type !== undefined || b.coordinates !== undefined || b.aliases !== undefined,
    {
      message: "Nothing to update",
    },
  );
export type SitePatch = z.infer<typeof SitePatch>;

export const ListCreate = z.object({
  name: z.string().min(1).max(120),
  filter: z.record(z.unknown()).optional(),
  site_ids: z.array(z.number().int().positive()).max(5000).optional(),
});
export type ListCreate = z.infer<typeof ListCreate>;

export const ListPatch = z.object({ name: z.string().min(1).max(120) });

export const ListItemsAdd = z.object({
  site_ids: z.array(z.number().int().positive()).min(1).max(5000),
});
export type ListItemsAdd = z.infer<typeof ListItemsAdd>;

export const ExportRequest = z
  .object({
    list_id: z.number().int().positive().optional(),
    filter: z.record(z.unknown()).optional(),
    site_ids: z.array(z.number().int().positive()).max(5000).optional(),
    format: z.enum(["csv", "xlsx", "geojson"]),
    // "gary" is the stored identifier of the baseline column set: migrations/0004_exports_log.sql pins it
    // in a CHECK; the UI labels it "Baseline list". Rename proposed in docs/proposals/exports-columns-rename.md.
    columns: z.enum(["gary", "engineering", "full"]).default("gary"),
  })
  .refine((b) => b.list_id !== undefined || b.filter !== undefined || b.site_ids !== undefined, {
    message: "Provide list_id, filter or site_ids",
  });
export type ExportRequest = z.infer<typeof ExportRequest>;

const num = z.number().finite();
const numMap = z.record(z.string(), num);

/** Per-org overrides of the global config files (PRD §14.5). Admin only; merged over defaults. */
export const SettingsPatch = z
  .object({
    cooling: z
      .object({
        T_on_c: num.min(-10).max(40),
        dx_min_g_kg: num.min(0).max(10),
        epsilon: num.min(0.1).max(1),
        dT_max_c: num.min(1).max(40),
        sens_pct_per_c: numMap,
        air_flow_kg_s_per_mw: numMap,
        drain_fraction: num.min(0).max(0.5),
        price_usd_per_mwh: numMap,
      })
      .partial()
      .optional(),
    scoring: z
      .object({
        weights: numMap,
        site_bonus_per_extra_unit: num.min(0).max(50),
        tiers: z.object({ "1": num, "2": num, "3": num }).partial(),
        frame_fit: numMap,
        retrofit_angle: numMap,
        age_cutoff_year: z.number().int().min(1950).max(2100),
        summer_utilization_unknown: num.min(0).max(1),
      })
      .partial()
      .optional(),
    budgets: z
      .object({
        per_site_usd: num.min(0).max(100),
        per_run_usd: num.min(0).max(10000),
        monthly_usd: num.min(0).max(100000),
      })
      .partial()
      .optional(),
    band: z
      .object({ mw_min: num.min(0).max(2000), mw_max: num.min(0).max(2000) })
      .partial()
      .optional(),
  })
  .superRefine((b, ctx) => {
    const w = b.scoring?.weights;
    if (w) {
      const sum = Object.values(w).reduce((a, v) => a + v, 0);
      if (Math.abs(sum - 1) > 0.001) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["scoring", "weights"],
          message: `Weights must sum to 1 (got ${sum.toFixed(3)})`,
        });
      }
    }
    if (
      b.band?.mw_min !== undefined &&
      b.band?.mw_max !== undefined &&
      b.band.mw_min > b.band.mw_max
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["band"],
        message: "mw_min must not exceed mw_max",
      });
    }
  });
export type SettingsPatch = z.infer<typeof SettingsPatch>;

/** POST /api/enrich (PRD §13). */
export const EnrichRequest = z.object({
  site_ids: z.array(z.number().int().positive()).min(1).max(500),
  agents: z
    .array(
      z.enum([
        "site_resolver",
        "turbine_model_resolver",
        "inlet_cooling_detector",
        "ownership_resolver",
        "contact_researcher",
      ]),
    )
    .optional(),
  allow_paid_apis: z.boolean().default(false),
  budget_usd: z.number().positive().max(10_000).optional(),
  /**
   * `sweep`: the deep contact pass (Exa Agent Ultra and Firecrawl Agent over batches of sites, then
   * ContactResearcher per site). Always estimated against per_run_usd at the route.
   */
  contact_mode: z.enum(["standard", "sweep"]).optional(),
  /** sweep only: run discovery again for sites that already have one at the current version */
  force: z.boolean().optional(),
  /** sweep only: index of this request's first site within a sweep split over several requests, so batch staggering continues across them */
  sweep_offset: z.number().int().min(0).optional(),
});
export type EnrichRequest = z.infer<typeof EnrichRequest>;

/**
 * Sweep driver (apps/api/scripts/contact-sweep-driver.ts): the driver runs the long Exa Agent Ultra
 * and Firecrawl Agent waits locally and uses these two admin routes for the parts that must happen
 * inside Plenum (safeRun and budget checks, cost ledger, per-site workflows). Both name the
 * approved paid_spend request that covers the sites.
 */
export const DiscoveryPlan = z.object({
  approval_id: z.number().int().positive(),
  site_ids: z.array(z.number().int().positive()).min(1).max(25),
  force: z.boolean().optional(),
});
export type DiscoveryPlan = z.infer<typeof DiscoveryPlan>;

const LeadUrl = z.string().url().max(2000);
export const DiscoveryComplete = z.object({
  approval_id: z.number().int().positive(),
  instance: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/),
  providers: z
    .array(z.enum(["exa", "firecrawl"]))
    .min(1)
    .max(2),
  handle: z.object({
    exa_run_id: z.string().max(100).nullable(),
    firecrawl_job_id: z.string().max(100).nullable(),
    notes: z.array(z.string().max(500)).max(20),
  }),
  outcome: z.object({
    sites: z
      .array(
        z.object({
          site_id: z.number().int().positive(),
          people: z
            .array(
              z.object({
                name: z.string().min(1).max(200),
                title: z.string().max(300),
                source_url: LeadUrl,
                quote: z.string().max(2000),
                email: z.string().max(320).nullable(),
                phone: z.string().max(64).nullable(),
                via: z.enum(["exa-ultra", "firecrawl-agent"]),
              }),
            )
            .max(40),
          urls: z.array(LeadUrl).max(60),
        }),
      )
      .max(25),
    exa_usd: z.number().min(0).max(100),
    firecrawl_usd: z.number().min(0).max(100),
    firecrawl_credits: z.number().int().min(0).max(100_000),
    notes: z.array(z.string().max(500)).max(20),
  }),
});
export type DiscoveryComplete = z.infer<typeof DiscoveryComplete>;

/** POST /api/review/:claim_id (PRD §12.4, §13). */
export const ReviewAction = z.object({
  action: z.enum(["accept", "reject", "edit"]),
  value: z.unknown().optional(),
  note: z.string().max(300).optional(),
});
export type ReviewAction = z.infer<typeof ReviewAction>;

export const ApprovalDecision = z.object({
  decision: z.enum(["approve", "reject"]),
  note: z.string().max(300).optional(),
});
export type ApprovalDecision = z.infer<typeof ApprovalDecision>;

/** Dev-only seed for e2e (non-production). */
export const DevSeedClaim = z.object({
  entity_type: z.enum(["site", "unit"]),
  entity_id: z.number().int().positive(),
  field: z.string().min(1).max(60),
  value: z.unknown(),
  quote: z.string().min(12).max(300),
  url: z.string().url(),
  confidence: z.number().min(0).max(1).default(0.7),
  document_text: z.string().max(20_000).optional(),
});
export type DevSeedClaim = z.infer<typeof DevSeedClaim>;

/** Outreach board (PRD §12.1 lists, §6.2 outreach_events). */
export const OutreachStatus = z.enum([
  "new",
  "researched",
  "contacted",
  "replied",
  "meeting",
  "proposal",
  "won",
  "lost",
  "dnc",
]);
export type OutreachStatus = z.infer<typeof OutreachStatus>;

export const OutreachCreate = z.object({
  site_id: z.number().int().positive(),
  contact_id: z.number().int().positive().optional(),
  status: OutreachStatus,
  channel: z.enum(["email", "phone", "meeting", "linkedin", "other"]).optional(),
  note: z.string().max(1000).optional(),
});
export type OutreachCreate = z.infer<typeof OutreachCreate>;

/** Outreach composer (ZeroGPT detector and humanizer; docs/questions.md Q72). */
export const ComposeCheck = z.object({
  text: z.string().min(20).max(8000),
});
export type ComposeCheck = z.infer<typeof ComposeCheck>;

export const ComposeHumanize = z.object({
  text: z.string().min(20).max(8000),
  strength: z.enum(["Quality", "Balanced", "More Human"]).optional(),
  readability: z
    .enum(["High School", "University", "Doctorate", "Journalist", "Marketing"])
    .optional(),
  purpose: z.string().max(60).optional(),
});
export type ComposeHumanize = z.infer<typeof ComposeHumanize>;

export const NoteCreate = z.object({
  entity_type: z.enum(["site", "unit", "contact", "organization", "list"]),
  entity_id: z.number().int().positive(),
  body: z.string().min(1).max(4000),
});
export type NoteCreate = z.infer<typeof NoteCreate>;

/** Contacts (PRD §11, §13). The route file is a Protected Path (docs/proposals/contacts-route.md). */
export const ContactCreate = z.object({
  site_id: z.number().int().positive(),
  organization_id: z.number().int().positive().optional(),
  full_name: z.string().min(3).max(160),
  title: z.string().max(160).nullable().default(null),
  role_category: z.enum([
    "plant_manager",
    "operations",
    "maintenance",
    "engineering",
    "environmental",
    "asset_management",
    "procurement",
    "executive",
    "other",
  ]),
  email: z.string().email().nullable().default(null),
  phone: z.string().max(40).nullable().default(null),
  phone_type: z.enum(["office", "plant_main", "mobile", "unknown"]).nullable().default(null),
  public_profile_url: z.string().url().nullable().default(null),
  evidence: z
    .array(
      z.object({
        url: z.string().url(),
        quote: z.string().min(12).max(300),
        retrieved_at: z.string().datetime(),
        source_type: z.string(),
      }),
    )
    .min(1),
});
export type ContactCreate = z.infer<typeof ContactCreate>;

export const ContactPatch = z
  .object({
    action: z.enum(["verify", "dnc", "clear_dnc", "edit", "accept", "reject"]),
    dnc_reason: z.string().max(300).optional(),
    fields: z
      .object({
        title: z.string().max(160).nullable(),
        role_category: ContactCreate.shape.role_category,
        email: z.string().email().nullable(),
        phone: z.string().max(40).nullable(),
        phone_type: z.enum(["office", "plant_main", "mobile", "unknown"]).nullable(),
        public_profile_url: z.string().url().nullable(),
      })
      .partial()
      .optional(),
    note: z.string().max(300).optional(),
  })
  .refine((b) => b.action !== "edit" || (b.fields && Object.keys(b.fields).length > 0), {
    message: "edit needs fields",
  })
  .refine((b) => b.action !== "dnc" || Boolean(b.dnc_reason), { message: "dnc needs a reason" });
export type ContactPatch = z.infer<typeof ContactPatch>;
